import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ShoppingBag,
  Heart,
  MapPin,
  User as UserIcon,
  KeyRound,
  CheckCircle2,
  MailCheck,
  Trash2,
  Star,
  Plus,
} from "lucide-react";
import Logo from "@/components/Logo";
import ProductCard from "@/components/ProductCard";

export default function MyAccount() {
  const { user, logout, refreshUser } = useApp();

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16" data-testid="account-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-6 mb-12">
        <div className="flex items-start gap-4">
          <Logo variant="monogram" className="h-14 w-14 dark:invert hidden sm:block" loading="eager" />
          <div>
            <p className="label-xs opacity-60">My Account</p>
            <h1 className="mt-3 text-5xl font-serif tracking-tighter" data-testid="account-greeting">
              Hello, {user?.name || "friend"}
            </h1>
            <p className="mt-2 opacity-70">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {user?.is_admin && (
            <Link to="/admin">
              <Button variant="outline" className="rounded-none border-foreground label-xs" data-testid="account-admin-btn">
                Admin Panel
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            onClick={logout}
            className="rounded-none border-foreground label-xs"
            data-testid="logout-btn"
          >
            Sign Out
          </Button>
        </div>
      </div>

      {!user?.email_verified && !user?.is_admin && (
        <VerifyBanner refresh={refreshUser} />
      )}

      <Tabs defaultValue="orders">
        <TabsList className="rounded-none flex-wrap h-auto">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none label-xs gap-2">
            <ShoppingBag className="h-3.5 w-3.5" /> Orders
          </TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist" className="rounded-none label-xs gap-2">
            <Heart className="h-3.5 w-3.5" /> Wishlist
          </TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses" className="rounded-none label-xs gap-2">
            <MapPin className="h-3.5 w-3.5" /> Addresses
          </TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details" className="rounded-none label-xs gap-2">
            <UserIcon className="h-3.5 w-3.5" /> Details
          </TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password" className="rounded-none label-xs gap-2">
            <KeyRound className="h-3.5 w-3.5" /> Password
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-8">
          <OrdersPanel />
        </TabsContent>
        <TabsContent value="wishlist" className="mt-8">
          <WishlistPanel />
        </TabsContent>
        <TabsContent value="addresses" className="mt-8">
          <AddressesPanel />
        </TabsContent>
        <TabsContent value="details" className="mt-8">
          <DetailsPanel />
        </TabsContent>
        <TabsContent value="password" className="mt-8">
          <PasswordPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Verify banner ---------------- */
function VerifyBanner({ refresh }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/customer/resend-verify");
      setSent(true);
      toast.success("Verification email sent. Please check your inbox.");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="mb-8 border p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      data-testid="verify-banner"
    >
      <div className="flex items-start gap-3">
        <MailCheck className="h-5 w-5 mt-0.5" />
        <div>
          <p className="font-serif text-lg">Verify your email</p>
          <p className="text-sm opacity-70">A short confirmation keeps your account secure.</p>
        </div>
      </div>
      <Button
        onClick={resend}
        disabled={busy || sent}
        className="rounded-none label-xs h-10 px-5"
        data-testid="resend-verify-btn"
      >
        {sent ? "Sent" : busy ? "Sending…" : "Resend Email"}
      </Button>
    </div>
  );
}

/* ---------------- Orders ---------------- */
function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/customer/orders")
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <p className="label-xs opacity-60 text-center py-12">Loading orders…</p>;
  if (!orders.length) {
    return (
      <div className="border border-dashed p-10 text-center" data-testid="orders-empty">
        <p className="opacity-70">No orders yet.</p>
        <Link to="/shop" className="mt-4 inline-block label-xs link-underline">
          Discover the collection
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="orders-panel">
      {orders.map((o) => (
        <OrderCard key={o.id} order={o} />
      ))}
    </div>
  );
}

function statusChip(status) {
  const s = String(status || "").toLowerCase();
  const map = {
    placed: "border-foreground/40",
    confirmed: "bg-foreground text-background border-foreground",
    packed: "bg-foreground text-background border-foreground",
    shipped: "bg-foreground text-background border-foreground",
    delivered: "bg-foreground text-background border-foreground",
  };
  return `inline-block border px-2.5 py-1 label-xs ${map[s] || "border-foreground/40"}`;
}

function OrderCard({ order }) {
  const items = order.items || [];
  return (
    <div className="border p-6" data-testid={`order-card-${order.order_no}`}>
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <p className="font-serif text-xl" data-testid={`order-no-${order.order_no}`}>
            {order.order_no}
          </p>
          <p className="text-xs opacity-60 mt-1">
            {new Date(order.created_at).toLocaleString()} · {items.length} item{items.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="text-right">
          <p className="font-body text-lg">{money(order.total)}</p>
          <span className={statusChip(order.status)} data-testid={`order-status-${order.order_no}`}>
            {order.status}
          </span>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-4">
        {items.map((it, i) => (
          <div
            key={`${order.id}-${i}`}
            className="flex gap-3 items-center"
            data-testid={`order-item-${order.order_no}-${i}`}
          >
            {it.image && (
              <img src={it.image} alt={it.name} className="w-16 h-20 object-cover" loading="lazy" />
            )}
            <div className="min-w-[120px]">
              <p className="text-sm">{it.name}</p>
              <p className="text-xs opacity-60">
                Size {it.size} · Qty {it.qty}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex justify-end">
        <Link to={`/track/${order.order_no}`}>
          <Button variant="outline" className="rounded-none border-foreground label-xs">
            Track order
          </Button>
        </Link>
      </div>
    </div>
  );
}

/* ---------------- Wishlist ---------------- */
function WishlistPanel() {
  const { wishlist, toggleWishlist, addToCart } = useApp();
  if (!wishlist.length) {
    return (
      <div className="border border-dashed p-10 text-center" data-testid="wishlist-empty">
        <p className="opacity-70">Your wishlist is empty.</p>
        <Link to="/shop" className="mt-4 inline-block label-xs link-underline">
          Save your first favorite
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="wishlist-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
        {wishlist.map((p, i) => (
          <div key={p.id} className="flex flex-col gap-3">
            <ProductCard product={p} index={i} />
            <div className="flex gap-2">
              <Button
                className="flex-1 rounded-none label-xs h-10"
                onClick={() => {
                  const size = (p.sizes || ["M"])[0];
                  addToCart(p, size, 1);
                  toggleWishlist(p);
                }}
                data-testid={`wishlist-move-cart-${p.id}`}
              >
                Move to Cart
              </Button>
              <button
                onClick={() => toggleWishlist(p)}
                className="h-10 w-10 border grid place-items-center"
                aria-label="Remove"
                data-testid={`wishlist-remove-${p.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Addresses ---------------- */
function AddressesPanel() {
  const [addresses, setAddresses] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api.get("/customer/addresses").then(({ data }) => {
      setAddresses(data.addresses || []);
      setDefaultId(data.default_address_id || null);
    });
  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!confirm("Delete this address?")) return;
    await api.delete(`/customer/addresses/${id}`);
    toast.success("Address deleted");
    load();
  };
  const setDefault = async (id) => {
    await api.post(`/customer/addresses/${id}/default`);
    load();
  };

  return (
    <div data-testid="addresses-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="label-xs opacity-60">{addresses.length} saved</p>
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="rounded-none label-xs h-10 px-4 gap-2"
          data-testid="address-new-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Address
        </Button>
      </div>
      {addresses.length === 0 && !showForm && (
        <div className="border border-dashed p-10 text-center opacity-70">
          No addresses yet. Add your first delivery address.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {addresses.map((a) => (
          <div key={a.id} className="border p-5" data-testid={`address-${a.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-serif text-lg">{a.full_name}</p>
                <p className="text-xs opacity-70 mt-1">{a.phone}</p>
              </div>
              {defaultId === a.id && (
                <span className="label-xs border px-2 py-1" data-testid={`address-default-${a.id}`}>
                  Default
                </span>
              )}
            </div>
            <p className="mt-3 text-sm leading-relaxed">
              {a.line1}
              {a.line2 ? `, ${a.line2}` : ""}
              <br />
              {a.city}, {a.state} {a.pincode}
              <br />
              {a.country || "India"}
            </p>
            <div className="mt-5 flex gap-2 flex-wrap">
              {defaultId !== a.id && (
                <button
                  onClick={() => setDefault(a.id)}
                  className="label-xs link-underline"
                  data-testid={`address-set-default-${a.id}`}
                >
                  Set as default
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={() => {
                  setEditing(a);
                  setShowForm(true);
                }}
                className="label-xs link-underline"
                data-testid={`address-edit-${a.id}`}
              >
                Edit
              </button>
              <button
                onClick={() => del(a.id)}
                className="label-xs link-underline"
                data-testid={`address-delete-${a.id}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <AddressForm
          initial={editing}
          onDone={() => {
            setShowForm(false);
            setEditing(null);
            load();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function AddressForm({ initial, onDone, onCancel }) {
  const [form, setForm] = useState({
    full_name: initial?.full_name || "",
    phone: initial?.phone || "",
    line1: initial?.line1 || "",
    line2: initial?.line2 || "",
    city: initial?.city || "",
    state: initial?.state || "",
    pincode: initial?.pincode || "",
    country: initial?.country || "India",
    is_default: !!initial?.is_default,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) {
        await api.patch(`/customer/addresses/${initial.id}`, form);
        toast.success("Address updated");
      } else {
        await api.post("/customer/addresses", form);
        toast.success("Address saved");
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-8 border p-6 grid gap-4" data-testid="address-form">
      <p className="label-xs">{initial ? "Edit address" : "New address"}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-xs">Full name</Label>
          <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-none mt-2" data-testid="af-name" />
        </div>
        <div>
          <Label className="label-xs">Phone</Label>
          <Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none mt-2" data-testid="af-phone" />
        </div>
      </div>
      <div>
        <Label className="label-xs">Address line 1</Label>
        <Input required value={form.line1} onChange={(e) => set("line1", e.target.value)} className="rounded-none mt-2" data-testid="af-line1" />
      </div>
      <div>
        <Label className="label-xs">Address line 2 (optional)</Label>
        <Input value={form.line2} onChange={(e) => set("line2", e.target.value)} className="rounded-none mt-2" data-testid="af-line2" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="label-xs">City</Label>
          <Input required value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-none mt-2" data-testid="af-city" />
        </div>
        <div>
          <Label className="label-xs">State</Label>
          <Input required value={form.state} onChange={(e) => set("state", e.target.value)} className="rounded-none mt-2" data-testid="af-state" />
        </div>
        <div>
          <Label className="label-xs">Pincode</Label>
          <Input required value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="rounded-none mt-2" data-testid="af-pincode" />
        </div>
      </div>
      <label className="flex items-center gap-3 select-none">
        <input
          type="checkbox"
          checked={form.is_default}
          onChange={(e) => set("is_default", e.target.checked)}
          className="h-4 w-4 accent-foreground"
          data-testid="af-default"
        />
        <span className="text-sm">Set as default delivery address</span>
      </label>
      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="rounded-none border-foreground label-xs"
          data-testid="af-cancel"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={busy} className="rounded-none label-xs" data-testid="af-submit">
          {busy ? "Saving…" : initial ? "Save Changes" : "Save Address"}
        </Button>
      </div>
    </form>
  );
}

/* ---------------- Details (name, phone) ---------------- */
function DetailsPanel() {
  const { user, refreshUser } = useApp();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
  }, [user?.user_id]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/customer/me", { name, phone });
      toast.success("Details updated");
      refreshUser?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-md space-y-4 border p-6" data-testid="details-panel">
      <p className="label-xs">Account Details</p>
      <div>
        <Label className="label-xs">Full name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-2" data-testid="details-name" />
      </div>
      <div>
        <Label className="label-xs">Email</Label>
        <Input value={user?.email || ""} disabled className="rounded-none mt-2 opacity-70" />
        <p className="mt-1 text-[11px] opacity-60">
          {user?.email_verified ? (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Verified
            </span>
          ) : (
            "Not verified"
          )}
        </p>
      </div>
      <div>
        <Label className="label-xs">Phone</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none mt-2" data-testid="details-phone" />
      </div>
      <Button type="submit" disabled={busy} className="rounded-none label-xs w-full h-11" data-testid="details-save">
        {busy ? "Saving…" : "Save"}
      </Button>
    </form>
  );
}

/* ---------------- Password ---------------- */
function PasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/customer/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-4 border p-6" data-testid="password-panel">
      <p className="label-xs">Change Password</p>
      <div>
        <Label className="label-xs">Current password</Label>
        <Input required type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="rounded-none mt-2" data-testid="pw-current" />
      </div>
      <div>
        <Label className="label-xs">New password</Label>
        <Input required minLength={8} type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-none mt-2" data-testid="pw-new" />
      </div>
      <div>
        <Label className="label-xs">Confirm new password</Label>
        <Input required minLength={8} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none mt-2" data-testid="pw-confirm" />
      </div>
      <Button type="submit" disabled={busy} className="rounded-none label-xs w-full h-11" data-testid="pw-submit">
        {busy ? "Updating…" : "Update Password"}
      </Button>
    </form>
  );
}
